<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { ComponentExposed } from 'vue-component-type-helpers'
import {
  Mutation,
  PromoteStepToModule as PromoteStepToModuleCommand,
  PromoteStepToModuleDataQuery,
  PromoteStepToModuleDataQueryVariables,
} from '@/generated/graphql'
import ModuleUpsertDialog, { ModuleFields } from '@/components/module/ModuleUpsertDialog.vue'

const props = defineProps<{
  bopId: string
  moduleId: string
  stepId: string
  graphNodeStepId: string
}>()
const emit = defineEmits<{
  promoted: [moduleId: string]
}>()

const { t } = useI18n()

const fetchQuery = useQuery<PromoteStepToModuleDataQuery, PromoteStepToModuleDataQueryVariables>(
  gql`
    query PromoteStepToModuleData($moduleId: ID!, $stepId: ID!) {
      product {
        module(id: $moduleId) {
          id
          nodes {
            id
            parentId
          }
        }
        step(id: $stepId) {
          id
          name
        }
      }
    }
  `,
  () => ({ moduleId: props.moduleId, stepId: props.stepId }),
)
const module = computed(() => fetchQuery.result.value?.product.module)
const step = computed(() => fetchQuery.result.value?.product.step)

const nodes = computed(() => module.value?.nodes ?? [])
const substepNodes = computed(() => descendantsOf(props.graphNodeStepId))
function descendantsOf(nodeId: string): typeof nodes.value {
  return nodes.value.filter((n) => n.parentId == nodeId).flatMap((n) => [n, ...descendantsOf(n.id)])
}

const promoteMutation = useMutation<Mutation, { command: PromoteStepToModuleCommand }>(gql`
  mutation PromoteStepToModule($command: PromoteStepToModule!) {
    product {
      promoteStepToModule(command: $command) {
        id
      }
    }
  }
`)

const dialog = ref<ComponentExposed<typeof ModuleUpsertDialog>>()
const open = async () => {
  await fetchQuery.refetch()
  await dialog.value?.open({
    create: true,
    initial: { name: step.value?.name },
    save: promote,
  })
}

async function promote(fields: ModuleFields) {
  const response = await promoteMutation.mutate({
    command: {
      graphNodeStepId: props.graphNodeStepId,
      name: fields.name,
      abbreviation: fields.abbreviation,
      description: fields.description,
    },
  })
  const moduleId = response?.data?.product?.promoteStepToModule?.id
  if (moduleId) {
    emit('promoted', moduleId)
  }
}

defineExpose({ open })
</script>

<template>
  <module-upsert-dialog
    ref="dialog"
    :bop-id="props.bopId"
    :title="t('component.promoteStepToModule.title')"
  >
    <p class="text-medium-emphasis text-body-2 mb-4">
      {{
        substepNodes.length > 0
          ? t('component.promoteStepToModule.hintSubsteps', { count: substepNodes.length })
          : t('component.promoteStepToModule.hintStep')
      }}
    </p>
  </module-upsert-dialog>
</template>

<style scoped lang="scss"></style>
